/* eslint-disable import/no-unresolved */
import BaseElement from 'base-element'
import { css, html, classMap, styleMap } from 'lit-all'
import styles from 'lib-styles'
import { handleize } from 'lib-utils'
/* eslint-disable import/no-unresolved */

class HHVariantPicker extends BaseElement {
  static properties = {
    product: {
      type: Object,
    },
    variants: {
      type: Array,
    },
    options: {
      type: Array,
    },
    _selected: {
      state: true,
    },
    firstSelected: {
      type: Object,
    },
    _optionsMap: {
      state: true,
    },
    productHandle: {
      type: String,
      attribute: 'product-handle',
      reflect: true,
    },
  }

  constructor() {
    super()
    this.product = {}
    this.variants = []
    this.options = []
    this._selected = {}
    this.firstSelected = {}
    this._optionsMap = []
  }

  willUpdate(changedProperties) {
    if (changedProperties.has('options')) {
      this._optionsMap = this._buildOptionsMap()
    }
  }

  firstUpdated(changedProperties) {
    if (changedProperties.has('variants')) {
      this._selected = this._getFirstAvailable()
      this._setChecked(this._selected)
      this.dispatchEvent(
        new CustomEvent('changeVariant', {
          bubbles: true,
          composed: true,
          detail: {
            handle: this.product.handle,
            variant: this._selected,
            preventScroll: true,
          },
        })
      )
    }

    if (changedProperties.has('options')) {
      // Set intial statuses
      for (let i = 0; i < this._optionsMap.length; i++) {
        this._setStatusesByOptionLevel(i)
      }
    }

    if (changedProperties.has('product')) {
      this.productHandle = this.product.handle
      this.dispatchEvent(
        new CustomEvent('variantPicker:ready:handle', {
          bubbles: true,
          composed: true,
        })
      )
    }
  }

  _buildOptionsMap() {
    return this.options?.map((option) => {
      const formattedOption = {
        ...option,
        type: option.name?.toLowerCase() === 'color' ? 'swatch' : 'button',
      }
      const formattedValues = option.values.map((value) => {
        return {
          label: value,
          status: 'available',
        }
      })
      return { ...formattedOption, values: formattedValues }
    })
  }

  _mapOptionValuesToNames(optionValues) {
    return optionValues.map((option, index) => ({
      name: this.options[index].name,
      value: option,
    }))
  }

  _setStatusesByOptionLevel(level) {
    const selectedOptions = Object.values(this._getCheckedOptionValues())
    const { values } = this._optionsMap[level]
    values.forEach((optionValue, optionValueIndex) => {
      // Get all selected values up to the current level
      const valuesToMatch = [
        ...selectedOptions.slice(0, level),
        optionValue.label,
      ]

      const matches = this.variants.filter((variant) =>
        valuesToMatch.every((value) => variant.options.includes(value))
      )

      const hasPossibleCombo = matches.length > 0
      const comboInStock = matches.some((variant) => variant.available)

      // Update swatch values and classes
      const optionId = `option${level + 1}.${optionValueIndex + 1}`
      const optionLabel = this.renderRoot.querySelector(
        `label[for="${optionId}"]`
      )
      const optionParent = optionLabel.closest('li')
      const optionInput = this.renderRoot.querySelector(
        `input[id="${optionId}"]`
      )

      this._optionsMap[level].status = !hasPossibleCombo
        ? 'missing'
        : !comboInStock
          ? 'sold-out'
          : 'available'

      // This internal setting is used to determine if the option should be disabled only on the first level or on all levels depending on the value of onlyDisableFirstLevel
      const onlyDisableFirstLevel = false
      // only run on the first level if onlyDisableFirstLevel is true,
      // or it will run on all levels if onlyDisableFirstLevel is false.
      if (!onlyDisableFirstLevel || level === 0) {
        optionInput.disabled = !comboInStock
      }

      optionLabel.classList.toggle('missing', !hasPossibleCombo)
      optionParent.classList.toggle('has-missing', !hasPossibleCombo)
      optionLabel.classList.toggle('sold-out', !comboInStock)
    })
  }

  _getCheckedOptionValues() {
    const optionGroupElements =
      this.renderRoot.querySelectorAll('.option-group')
    const checked = {}
    for (const item of optionGroupElements) {
      const selectedValue = item.querySelector('.option-value:checked')
      if (selectedValue)
        checked[item.getAttribute('option')] = selectedValue.value
    }
    return checked
  }

  _getSelectedVariant() {
    const variantSelectedOptions = this._getCheckedOptionValues()

    if (!Object.entries(variantSelectedOptions).length) return null

    for (const variant of this.variants) {
      const selectedOptions = this._mapOptionValuesToNames(variant.options)
      const optionMap = selectedOptions.reduce((acc, option) => {
        acc[option.name] = option.value
        return acc
      }, {})
      if (
        Object.keys(variantSelectedOptions).every(
          (key) => optionMap[key] === variantSelectedOptions[key]
        )
      ) {
        return variant
      }
    }

    return null // Add this line to return null if no variant is found
  }

  _setChecked(selection) {
    const optionGroupElements =
      this.renderRoot.querySelectorAll('.option-group')
    optionGroupElements.forEach((optionGroup, optionIndex) => {
      const optionValues = optionGroup.querySelectorAll('.option-value')
      for (const optionValue of optionValues) {
        if (optionValue.value === selection.options[optionIndex]) {
          optionValue.setAttribute('checked', '')
          optionValue.checked = true
        } else {
          optionValue.removeAttribute('checked')
          optionValue.checked = false
        }
      }
    })
  }

  _getFirstAvailable(level = false) {
    let result
    if (level === false && this.firstSelected) {
      result = this.firstSelected
    } else if (level === false) {
      result =
        this.variants.find((variant) => variant.available) || this.variants[0]
    } else {
      const selectedOptions = Object.values(this._getCheckedOptionValues())
      // Check if full combo is available
      result = this._getSelectedVariant()
      // Otherwise find the first available variant for the subsequent level
      if (!result || !result.available) {
        result = this.variants.find((variant) => {
          return (
            variant.options.every((option, i) => {
              if (i === level) return true
              return option === selectedOptions[i]
            }) && variant.available
          )
        })
      }
      // When no first available variant is found, fallback on first available for last level
      if (!result) {
        result = this.variants.find((variant) => {
          for (let i = 0; i < variant.options.length - 1; i++) {
            if (variant.options[i] !== selectedOptions[i]) {
              return false
            }
          }
          return true
        })
      }

      if (!result) {
        result = this.variants.find(
          (variant) => variant.options[0] === selectedOptions[0]
        )
      }
    }
    return result || null
  }

  _handleOptionClick(event) {
    const { currentTarget } = event
    const level = Number(currentTarget.dataset.level)

    currentTarget.setAttribute('checked', true)
    if (level < this._optionsMap.length) {
      this._selected = this._getFirstAvailable(level + 1)
    } else {
      this._selected = this._getSelectedVariant()
    }
    this._setChecked(this._selected)
    for (let i = level + 1; i < this._optionsMap.length; i++) {
      this._setStatusesByOptionLevel(i)
    }
    this.dispatchEvent(
      new CustomEvent('changeVariant', {
        bubbles: true,
        composed: true,
        detail: { handle: this.product.handle, variant: this._selected },
      })
    )
  }

  render() {
    return html`
      ${this._optionsMap.map((option, i) => {
        const presentOptionsLength =
          this._optionsMap[i].values.filter(
            (value) => value.status !== 'missing'
          )?.length || 0
        const isSwatch = option.type === 'swatch'
        const listClasses = {
          'option-group__list': true,
          flex: true,
          wrap: true,
          gap1: isSwatch,
          gap075: !isSwatch,
        }
        return html`
          <fieldset class="option-group" option=${option.name}>
            ${option.name === 'Title' && option.values[0] === 'Default Title'
              ? ''
              : html`
                  <legend class="h7">
                    ${option.name}: ${this._selected?.[`option${i + 1}`]}
                  </legend>
                `}
            <ul
              class=${classMap(listClasses)}
              ?hidden=${presentOptionsLength === 1 && !isSwatch}
            >
              ${option.values.map((value, j) => {
                const labelClasses = {
                  swatch: isSwatch,
                  'option-label': true,
                }
                const labelStyles = {}
                if (isSwatch) {
                  const swatchColorConfig =
                    __THEME_GLOBALS__?.swatches[handleize(value.label)]
                  if (swatchColorConfig) {
                    if (swatchColorConfig[0]) {
                      labelStyles.background = swatchColorConfig[0]
                    }
                    if (swatchColorConfig[1]) {
                      labelStyles['--secondary-color'] = swatchColorConfig[1]
                    }
                  }
                }
                return html`
                  <li>
                    <input
                      data-level=${i}
                      @change=${this._handleOptionClick}
                      class="option-value sr-only"
                      type="radio"
                      value=${value.label}
                      name=${`option${i + 1}`}
                      id=${`option${i + 1}.${j + 1}`}
                      ?disabled=${value.status !== 'available' && i === 0}
                      ?checked=${this._selected?.options?.[i] === value.label}
                    />
                    <label
                      for=${`option${i + 1}.${j + 1}`}
                      class=${classMap(labelClasses)}
                      style=${styleMap(labelStyles)}
                    >
                      ${value.label}
                    </label>
                    ${this.product.newColors.includes(value.label)
                      ? html`
                          <span class="new-color caption color-subdued">
                            New!
                          </span>
                        `
                      : ''}
                  </li>
                `
              })}
            </ul>
          </fieldset>
        `
      })}
    `
  }

  static styles = [
    styles.reset,
    styles.a11y,
    styles.flex,
    styles.typography,
    css`
      :host {
        display: flex;
        flex-direction: column;
        gap: 1em;
        position: relative;
      }
      .option-group {
        margin: 0;
        padding: 0;
      }
      .option-group__list[hidden] {
        display: none;
      }
      .option-group__list {
        margin-top: 1em;
      }
      .option-group__list .has-missing {
        display: none;
      }
      .h7 {
        margin: 0;
      }
      legend {
        margin-bottom: 0.25em;
        padding: 0;
      }
      label {
        align-items: center;
        border: solid 1px;
        border-radius: 4px;
        box-sizing: content-box;
        cursor: pointer;
        display: flex;
        font-size: 14px;
        font-weight: 400;
        justify-content: center;
        padding: 8px 14px;
      }
      label.swatch {
        border-radius: 50%;
        display: block;
        height: 27px;
        padding: 0;
        text-indent: -9999px;
        width: 27px;
        overflow: hidden;
        position: relative;
      }
      label.swatch::after {
        content: '';
        background-color: var(--secondary-color);
        display: block;
        height: 100%;
        width: 100%;
        position: absolute;
        top: 0;
        left: 50%;
      }
      .option-value:disabled + .option-label {
        cursor: default;
      }
      :checked + label:not(.swatch) {
        color: white;
        background-color: var(--color-black, black);
        border: solid 1px var(--color-black);
      }
      :checked + label.swatch {
        position: relative;
      }
      :checked + label.swatch {
        box-shadow:
          0 0 0 2px var(--color-body-background, #f5f5f5),
          0 0 0 3px var(--color-dark-green, black);
      }
      .product-option__cross-line {
        display: none;
      }
      label.sold-out {
        position: relative;
        overflow: hidden;
      }
      label.sold-out::before {
        content: '';
        background: linear-gradient(
          to right bottom,
          transparent calc(50% - 1px),
          white calc(50% - 1px),
          white 50%,
          black 50%,
          black calc(50% + 1px),
          transparent calc(50% + 1px)
        );
        position: absolute;
        inset: 0;
        z-index: 1;
      }
      label.missing {
        pointer-events: none;
      }
      label.missing:not(.swatch) {
        opacity: 0.5;
      }
      label.missing.swatch {
        position: relative;
        overflow: hidden;
      }
      label.missing.swatch::before {
        content: '';
        background: linear-gradient(
          to right bottom,
          transparent calc(50% - 1px),
          white calc(50% - 1px),
          white 50%,
          black 50%,
          black calc(50% + 1px),
          transparent calc(50% + 1px)
        );
        position: absolute;
        inset: 0;
        z-index: 1;
      }
    `,
  ]
}

customElements.define('hh-variant-picker', HHVariantPicker)
